import { forwardRef, useId } from 'react';

/**
 * A determinate or indeterminate progress bar (spec §7.2). Give it a visible
 * `label` (preferred — it doubles as the accessible name via
 * `aria-labelledby`) or an `ariaLabel` for icon-only contexts; never leave a
 * progressbar unnamed.
 *
 * @param {object} props
 * @param {number} [props.value=0]
 * @param {number} [props.max=100]
 * @param {import('react').ReactNode} [props.label] - visible label above the track.
 * @param {string} [props.ariaLabel] - accessible name when no visible `label` is given.
 * @param {boolean} [props.showValue=true] - show the rounded percentage next to the label.
 * @param {boolean} [props.indeterminate=false]
 * @param {string} [props.className]
 */
export const Progress = forwardRef(function Progress(
  { value = 0, max = 100, label, ariaLabel, showValue = true, indeterminate = false, className = '', ...rest },
  ref
) {
  const generatedId = useId();
  const labelId = label ? generatedId : undefined;
  const percent = indeterminate ? null : Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div ref={ref} className={`etch-progress ${className}`.trim()} {...rest}>
      {(label || showValue) && (
        <div className="etch-progress__label">
          {label && <span id={labelId}>{label}</span>}
          {showValue && !indeterminate && <span>{Math.round(percent)}%</span>}
        </div>
      )}
      <div
        className="etch-progress__track"
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-labelledby={labelId}
        aria-label={!label ? ariaLabel : undefined}
      >
        <div
          className="etch-progress__bar"
          data-indeterminate={indeterminate || undefined}
          style={indeterminate ? undefined : { width: `${percent}%` }}
        />
      </div>
    </div>
  );
});

export default Progress;
